import { useNavigate } from "react-router";
import { cardHeldExplanation } from "../../domain/practice/cards.ts";
import { useAppServices } from "../hooks/AppContext.tsx";
import { usePractice } from "../hooks/bindings.ts";
import { formatLocalDate } from "../../application/vocabulary/queries.ts";
import { Frame, PrimaryButton, Screen, TopBar } from "../components/Shell.tsx";
import { TypeTag } from "../components/TypeTag.tsx";
import styles from "../design-system/ui.module.css";

export function PracticeSummaryScreen() {
  const { practice } = useAppServices();
  const state = usePractice();
  const summary = practice.summary();
  const cardSummary = practice.cardSummary();
  const navigate = useNavigate();

  if (state.cardSession && cardSummary) {
    const direction = cardSummary.direction === "hide_meaning" ? "Hide meaning" : "Hide word or phrase";
    return (
      <Frame>
        <Screen withNav={false}>
          <TopBar title="Practice Summary" backTo="/practice" />
          <header className={styles.pageHead}>
            <h1>Card summary</h1>
            <p>
              Self-reported {direction}. {cardSummary.correctCount} correct, {cardSummary.incorrectCount} incorrect,{" "}
              {cardSummary.skippedCount} skipped
              {cardSummary.unfinishedCount ? `, ${cardSummary.unfinishedCount} unfinished` : ""}.
            </p>
            {state.cardSession.unsavedExit ? (
              <p className={styles.error}>The last result was not saved, so it is not included as a grade.</p>
            ) : null}
          </header>
          {cardSummary.items.map((item) => (
            <article key={item.itemId} className={styles.detail}>
              <h2>
                {item.wordDisplay} · <TypeTag type={item.type} />
              </h2>
              <p>{item.shortLabel}</p>
              <p>
                {item.result === "correct"
                  ? "Correct"
                  : item.result === "incorrect"
                    ? "Incorrect"
                    : item.result === "skipped"
                      ? "Skipped"
                      : "Unfinished"}
              </p>
              {item.result === "correct" && item.heldReason ? (
                <p className={styles.sub}>{cardHeldExplanation(item.heldReason, item.wasDue)}</p>
              ) : null}
              {item.nextDueAt ? <p className={styles.sub}>Next review {formatLocalDate(item.nextDueAt)}</p> : null}
              <button
                className={styles.textButton}
                type="button"
                onClick={() => {
                  practice.browseCard(item.index);
                  navigate("/practice/session");
                }}
              >
                Review card
              </button>
            </article>
          ))}
          <PrimaryButton
            onClick={() => {
              practice.clearSession();
              navigate("/practice");
            }}
          >
            Done
          </PrimaryButton>
        </Screen>
      </Frame>
    );
  }

  if (!state.session || !summary) {
    return (
      <Frame>
        <Screen withNav={false}>
          <TopBar title="Practice Summary" backTo="/practice" />
          <div className={styles.empty}>No session to summarize.</div>
        </Screen>
      </Frame>
    );
  }

  return (
    <Frame>
      <Screen withNav={false}>
        <TopBar title="Practice Summary" backTo="/practice" />
        <header className={styles.pageHead}>
          <h1>Practice Summary</h1>
          <p>
            {summary.correctCount} / {summary.correctCount + summary.reviewCount} correct on the first standard
            assessment.
          </p>
          {state.session.unsavedExit ? (
            <p className={styles.error}>The last result was not saved, so it is not included as committed progress.</p>
          ) : null}
        </header>
        <section className={styles.summaryGood}>
          <h2>Correct ({summary.correctCount})</h2>
          {summary.correct.length ? (
            <div className={styles.chips}>
              {summary.correct.map((item) => (
                <span className={styles.chip} key={item.meaningId}>
                  {item.wordDisplay}
                  {item.review?.nextDueAt ? ` · next ${formatLocalDate(item.review.nextDueAt)}` : ""}
                </span>
              ))}
            </div>
          ) : (
            <p className={styles.sub}>None this session.</p>
          )}
        </section>
        <section className={styles.summaryBad}>
          <h2>Needs review ({summary.reviewCount})</h2>
          {summary.needsReview.length ? (
            summary.needsReview.map((item) => (
              <article key={item.meaningId} className={styles.detail}>
                <h2>{item.review?.question}</h2>
                <p>
                  {item.wordDisplay} · <TypeTag type={item.type} /> · {item.shortLabel}
                </p>
                <p>
                  Your answer:{" "}
                  {item.review?.unknown ? "I don’t know" : item.review?.hintUsed ? "Assisted" : item.review?.givenAnswer}
                </p>
                <p>Correct answer: {item.review?.correctAnswer}</p>
                <p>{item.review?.explanation}</p>
                {item.review?.nextDueAt ? <p className={styles.sub}>Next review {formatLocalDate(item.review.nextDueAt)}</p> : null}
                {item.review?.relatedWordDistinctions.map((distinction) => (
                  <p key={distinction.meaningId}>{distinction.explanation}</p>
                ))}
              </article>
            ))
          ) : (
            <p className={styles.sub}>Nothing to review.</p>
          )}
        </section>
        {summary.supporting.length ? (
          <section className={styles.detail}>
            <h2>Supporting practice ({summary.supportingCount})</h2>
            <p className={styles.sub}>These items did not change interval stages.</p>
            <div className={styles.chips}>
              {summary.supporting.map((item) => (
                <span className={styles.chip} key={`${item.meaningId}-supporting`}>
                  {item.wordDisplay}
                </span>
              ))}
            </div>
          </section>
        ) : null}
        {summary.unattempted.length ? (
          <p className={styles.sub}>
            Not attempted: {summary.unattempted.map((item) => item.wordDisplay).join(", ")}
          </p>
        ) : null}
        <PrimaryButton
          onClick={() => {
            practice.clearSession();
            navigate("/practice");
          }}
        >
          Done
        </PrimaryButton>
      </Screen>
    </Frame>
  );
}
